/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { SecurityHelper, useFlags } from 'sarsaparilla';
import { authModalActions } from 'shared-ui/auth/redux';
import { gnoment } from 'shared-ui/gnoment';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import { LotteryApplicationStatuses } from 'ui-permit/shared-components/utils/enums';
import { orderDetailsPageRoute } from '../../dev/routesArray';
import { permitActions, lotteryActions } from '../actions';
import { permitSelectors, lotterySelectors } from '../reducers';
import { RES_ID_DELIMITER } from '../constants';
import { LOCATION_TZ } from '../constants/hunting-permit-constants';
import LotteryWinnerConfirmation from '../components/lottery-winner-confirmation/LotteryWinnerConfirmation';

export function LotteryWinnerConfirmationPage({
    fetchPermit,
    fetchRecarea,
    fetchLottery,
    fetchLotteryApplication,
    acceptLotteryApplication,
    declineLotteryApplication,
    openLoginModal,
    permit,
    lottery,
    lotteryApplication,
    isAccepting,
    isDeclining,
    isLoading,
    errors,
}) {
    const { permitId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        const loggedIn = SecurityHelper.isLoggedIn();
        if (!loggedIn) openLoginModal();
    }, [openLoginModal]);

    useEffect(() => {
        fetchPermit(permitId).then(({ recarea }) => fetchRecarea(recarea));
    }, [fetchPermit, fetchRecarea, permitId]);

    useEffect(() => {
        const issuanceId = searchParams.get('id');
        fetchLotteryApplication(permitId, issuanceId).then(({ lottery_id }) =>
            fetchLottery(permitId, lottery_id)
        );
    }, [fetchLotteryApplication, fetchLottery, permitId, searchParams]);

    const routeToOrderDetailsPage = ({ id, permit_id }) => {
        const reservationId = `${id}${RES_ID_DELIMITER}${permit_id}`;
        navigate(orderDetailsPageRoute(permit_id, reservationId));
    };

    const { huntingMuxMigrationUseNewPaths } = useFlags();

    const handleLotteryAccept = () => {
        const loggedIn = SecurityHelper.isLoggedIn();
        if (!loggedIn) openLoginModal();
        acceptLotteryApplication(
            lotteryApplication.permit_id,
            lotteryApplication.id,
            huntingMuxMigrationUseNewPaths
        ).then((res) => {
            routeToOrderDetailsPage(res);
        });
    };

    const handleLotteryDecline = () => {
        const { id: issuanceId } = lotteryApplication;
        declineLotteryApplication(
            permitId,
            issuanceId,
            huntingMuxMigrationUseNewPaths
        ).then(() => {
            window.location.assign('/account/reservations/lotteries');
        });
    };

    const isValidLotteryAward = () => {
        const permitTZ = LOCATION_TZ[permit.id];
        if (lotteryApplication.status === LotteryApplicationStatuses.LOTTERY_AWARDED) {
            return true;
        }

        if (lottery?.lottery?.announced_at) {
            const currentTime = gnoment().tz(permitTZ);
            const announcedTime = gnoment(lottery.lottery.announced_at).tz(permitTZ);
            if (announcedTime.isBefore(currentTime)) {
                return true;
            }
        }

        return false;
    };

    return (
        <LotteryWinnerConfirmation
            isAccepting={isAccepting}
            isDeclining={isDeclining}
            isLoading={isLoading}
            errors={errors}
            isValidAwardedApp={isValidLotteryAward()}
            permit={permit}
            lottery={lottery}
            lotteryApplication={lotteryApplication}
            recareaName={get(permit.recarea, '[0].name', '')}
            handleLotteryAccept={handleLotteryAccept}
            handleLotteryDecline={handleLotteryDecline}
        />
    );
}

LotteryWinnerConfirmationPage.propTypes = {
    permit: PropTypes.object,
    lottery: PropTypes.object /*LotteryType*/,
    lotteryApplication: PropTypes.object /*HuntingLotteryIssuanceType*/,
    isAccepting: PropTypes.bool,
    isDeclining: PropTypes.bool,
    isLoading: PropTypes.bool,
    errors: PropTypes.any,
    openLoginModal: PropTypes.func,
    fetchRecarea: PropTypes.func,
    fetchPermit: PropTypes.func,
    fetchLottery: PropTypes.func,
    fetchLotteryApplication: PropTypes.func,
    declineLotteryApplication: PropTypes.func,
    acceptLotteryApplication: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => ({
    fetchPermit: bindActionCreators(permitActions.fetchPermit, dispatch),
    fetchRecarea: bindActionCreators(permitActions.fetchRecarea, dispatch),
    fetchLottery: bindActionCreators(lotteryActions.fetchLottery, dispatch),
    fetchLotteryApplication: bindActionCreators(
        lotteryActions.fetchLotteryIssuance,
        dispatch
    ),
    declineLotteryApplication: bindActionCreators(
        lotteryActions.cancelLotteryIssuance,
        dispatch
    ),
    acceptLotteryApplication: bindActionCreators(
        lotteryActions.acceptLotteryIssuance,
        dispatch
    ),
    openLoginModal: bindActionCreators(authModalActions.openLoginModal, dispatch),
});

const mapStateToProps = (state) => ({
    permit: permitSelectors.getPermit(state),
    recarea: permitSelectors.getRecarea(state),
    lottery: lotterySelectors.getLottery(state),
    lotteryApplication: lotterySelectors.getLotteryIssuance(state),
    isAccepting: lotterySelectors.getLotteryIssuanceAcceptLoading(state),
    isDeclining: lotterySelectors.getLotteryIssuanceCancelLoading(state),
    isLoading:
        permitSelectors.getPermitLoading(state) ||
        !permitSelectors.getPermitLoaded(state) ||
        lotterySelectors.getLotteryIssuanceLoading(state) ||
        !lotterySelectors.getLotteryIssuanceLoaded(state) ||
        (lotterySelectors.getLotteryLoading(state) &&
            !lotterySelectors.getLotteryLoaded(state)),
    errors: [
        // creating set to remove duplicate errors... (TODO: should errors from different calls all route to 1 error var?)
        ...new Set([
            permitSelectors.getPermitError(state),
            lotterySelectors.getLotteryIssuanceError(state),
            lotterySelectors.getLotteryError(state),
            lotterySelectors.getLotteryIssuanceCancelError(state),
            lotterySelectors.getLotteryIssuanceAcceptError(state),
        ]),
    ].filter((e) => !isEmpty(e)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(LotteryWinnerConfirmationPage);
